import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, SlidersHorizontal, History } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field, DrawerSection } from "@/components/forms/fields";
import { inventoryService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";
import { INVENTORY_ADJUST_TYPES } from "@/lib/constants";

const EMPTY = { adjustment_type: "add_stock", quantity: "", reason: "", notes: "" };

export function AdjustDrawer({ open, onOpenChange, record, storeName, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open, record]);

  const txnQuery = useQuery({
    queryKey: ["inventory", "transactions", record?.id],
    queryFn: () => inventoryService.transactions(record.id),
    enabled: open && !!record?.id,
  });
  const transactions = txnQuery.data?.items || [];

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const typeMeta = useMemo(() => INVENTORY_ADJUST_TYPES.find((t) => t.value === form.adjustment_type), [form.adjustment_type]);

  const current = record?.available ?? 0;
  const qty = Number(form.quantity) || 0;
  const projected = useMemo(() => {
    if (typeMeta?.mode === "set") return qty;
    if (typeMeta?.mode === "subtract") return current - qty;
    return current + qty;
  }, [typeMeta, qty, current]);

  const qtyError = form.quantity === "" || qty < 0 ? "Enter a valid quantity" : projected < 0 ? "Result cannot be negative" : "";
  const reasonError = !form.reason.trim() ? "Reason is required" : "";
  const canSave = !qtyError && !reasonError && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await inventoryService.adjust(record.id, {
        adjustment_type: form.adjustment_type,
        quantity: qty,
        reason: form.reason.trim(),
        notes: form.notes.trim(),
      });
      toast.success(`Stock adjusted — ${record.sku} now ${projected}`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={SlidersHorizontal}
      title="Adjust Inventory"
      description={record ? `${record.sku} · ${storeName || "—"}` : ""}
      widthClass="sm:max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="adjust-drawer-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="adjust-drawer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm adjustment"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-border bg-muted/40 px-4 py-3" data-testid="adjust-current">
            <p className="text-xs font-medium text-muted-foreground">Current Available</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{current}</p>
          </div>
          <div className="rounded-lg border border-primary/30 bg-brand-light px-4 py-3" data-testid="adjust-projected">
            <p className="text-xs font-medium text-primary">New Available</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-brand-dark">{projected}</p>
          </div>
        </div>

        <DrawerSection title="Adjustment">
          <Field label="Adjustment Type" required>
            <Select value={form.adjustment_type} onValueChange={set("adjustment_type")}>
              <SelectTrigger data-testid="adjust-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INVENTORY_ADJUST_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={typeMeta?.mode === "set" ? "New Counted Quantity" : "Adjustment Quantity"} required error={qtyError}>
            <Input type="number" min={0} value={form.quantity} onChange={(e) => set("quantity")(e.target.value)}
              placeholder="0" data-testid="adjust-quantity-input" />
          </Field>
          <Field label="Reason" required error={reasonError}>
            <Input value={form.reason} onChange={(e) => set("reason")(e.target.value)}
              placeholder="Cycle count correction" data-testid="adjust-reason-input" />
          </Field>
          <Field label="Notes">
            <Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} data-testid="adjust-notes-input" />
          </Field>
        </DrawerSection>

        <DrawerSection title="Recent adjustments">
          {txnQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : transactions.length === 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground" data-testid="adjust-history-empty">
              <History className="h-4 w-4" /> No manual adjustments yet.
            </div>
          ) : (
            <div className="space-y-2">
              {transactions.map((t) => (
                <div key={t.id} className="rounded-lg border border-border bg-muted/30 px-3 py-2" data-testid={`adjust-history-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{t.adjustment_label}</span>
                    <span className="text-sm font-semibold tabular-nums text-foreground">{t.previous_available} → {t.new_available}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t.reason} · {t.actor} · {formatRelativeTime(t.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </DrawerSection>
      </div>
    </Drawer>
  );
}
